import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';

/**
 * CommonQuestions Component
 *
 * Displays common questions students are asking across a course
 * Helps teachers identify patterns of confusion and address them proactively
 *
 * Part of the re-teach prevention system
 */
const CommonQuestions = ({ courseId, teacherId }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeWindow, setTimeWindow] = useState(7); // days

  useEffect(() => {
    fetchCommonQuestions();
  }, [courseId, timeWindow]);

  const fetchCommonQuestions = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');

      // Map days to period for backend
      let period = 'week';
      if (timeWindow === 1) period = 'day';
      else if (timeWindow >= 30) period = 'month';

      const params = new URLSearchParams({
        period: period,
        limit: 20
      });

      if (courseId) {
        params.append('course_id', courseId);
      }

      const response = await fetch(
        `/api/moderation/teacher/common-questions?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch common questions');
      }

      const data = await response.json();
      setQuestions(data.questions || []);
    } catch (err) {
      console.error('Error fetching common questions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddressInClass = (questionId) => {
    // Simple: Remove from list locally (teacher has acknowledged and will address it)
    setQuestions(prev => prev.filter(q => q.question_id !== questionId));
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'medium':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'low':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getPriorityIndicator = (priority) => {
    const colors = {
      high: 'bg-red-500',
      medium: 'bg-orange-400',
      low: 'bg-green-500',
      default: 'bg-gray-300'
    };
    return (
      <div className={`w-3 h-3 rounded-full ${colors[priority] || colors.default}`} />
    );
  };

  if (loading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Common Questions This Week</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading questions...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full border-red-200">
        <CardHeader>
          <CardTitle className="text-red-700">Error Loading Questions</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-red-600">{error}</p>
          <button
            onClick={fetchCommonQuestions}
            style={{
              marginTop: '16px',
              padding: '8px 16px',
              backgroundColor: '#297A92',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              transition: 'all 200ms',
              boxShadow: '0 2px 8px rgba(41, 122, 146, 0.25)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#42909E';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#297A92';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Try Again
          </button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader className="border-b border-gray-200">
        <div className="flex items-center justify-between">
          <CardTitle>Common Student Questions</CardTitle>
          <div className="flex items-center gap-2">
            <label htmlFor="timeWindow" className="text-sm text-gray-600">
              Show questions from:
            </label>
            <select
              id="timeWindow"
              value={timeWindow}
              onChange={(e) => setTimeWindow(parseInt(e.target.value))}
              style={{
                padding: '6px 12px',
                border: '1px solid #ABCACA',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'white',
                color: '#3D4954',
                cursor: 'pointer'
              }}
            >
              <option value={3}>Last 3 days</option>
              <option value={7}>Last week</option>
              <option value={14}>Last 2 weeks</option>
              <option value={30}>Last month</option>
            </select>
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-2">
          Patterns of confusion across multiple students that may need classroom attention
        </p>
      </CardHeader>

      <CardContent className="p-0">
        {questions.length === 0 ? (
          <div className="px-6 py-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-gray-600 text-lg font-medium">No common patterns yet</p>
            <p className="text-gray-500 text-sm mt-2">
              Students are asking unique questions. Try expanding the time window above to see more data.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {questions.map((question, index) => (
              <div
                key={question.question_id}
                className="p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1.5">
                    {getPriorityIndicator(question.priority)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={getPriorityColor(question.priority)}>
                        Asked by {question.student_count} student{question.student_count > 1 ? 's' : ''}
                      </Badge>
                      {question.suggests_misconception && (
                        <Badge className="bg-purple-100 text-purple-800 border-purple-300">
                          May indicate misconception
                        </Badge>
                      )}
                    </div>

                    <p className="text-gray-900 font-medium text-lg mb-2">
                      "{question.question_text}"
                    </p>

                    <div className="flex items-center gap-3 text-sm text-gray-600 mb-3">
                      {question.topic && (
                        <span className="inline-flex items-center px-2 py-1 bg-gray-100 rounded text-gray-700">
                          {question.topic}
                        </span>
                      )}
                      {question.course_name && (
                        <span className="text-gray-600">
                          {question.course_name}
                        </span>
                      )}
                      <span className="text-gray-500 capitalize">
                        {question.category ? question.category.replace('_', ' ') : 'general'}
                      </span>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleAddressInClass(question.question_id)}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#297A92',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 600,
                          transition: 'all 200ms',
                          boxShadow: '0 2px 8px rgba(41, 122, 146, 0.25)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#42909E';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#297A92';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        Address in Class
                      </button>
                    </div>

                    {question.priority === 'high' && question.student_count >= 8 && (
                      <div className="mt-3 p-3 bg-orange-50 border-l-4 border-orange-400 rounded">
                        <p className="text-sm text-orange-900">
                          <strong>Suggestion:</strong> Consider addressing this with the whole class
                          to help {question.student_count} students at once.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CommonQuestions;
